/* @sravni/react-design-system version 0.2.65 */
import { __rest, __read, __assign, __spreadArray } from 'tslib';
import * as React from 'react';
import { useBoolean, useStateCallback, isDefined, useStyles, error, safeInvoke, isFunction, isNull, noop, useKeyPress, getNextIndex, getPrevIndex } from '@sravni/react-utils';
import '../../Popover/index.js';
import { Menu } from '../../Menu/index.js';
import '../../Button/index.js';
import modules_c052660e from '../styles/Dropdown.module.less.js';
import '../../Typography/index.js';
import { Text } from '../../Typography/src/Text.js';
import Button from '../../Button/src/Button.js';
import Popover from '../../Popover/src/Popover.js';

var defaultEmptyContentText = 'К сожалению, не получилось загрузить список, скоро всё поправим';
var minWidthModifier = {
    name: 'minWidth',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: function (_a) {
        var state = _a.state;
        state.styles.popper.minWidth = '280px';
    },
};
/** Helper function, return object with positioner settings */
var makePositionerSettings = function (placement, matchWidth, flip) { return ({
    placement: placement,
    modifiers: matchWidth ? [] : [minWidthModifier],
    matchWidth: matchWidth,
    flip: flip,
}); };
/**
 * @description Dropdown component to select value from options
 *
 * @component
 * @example
 * ```jsx
 * <Dropdown options={options}>{triggerElement}</Dropdown>
 * ```
 */
var Dropdown = React.forwardRef(function (props, ref) {
    var _a;
    var defaultValue = props.defaultValue, value = props.value, _b = props.placement, placement = _b === void 0 ? 'bottom-start' : _b, mode = props.mode, mobile = props.mobile, _c = props.fullscreen, fullscreen = _c === void 0 ? true : _c, _d = props.matchWidth, matchWidth = _d === void 0 ? false : _d, options = props.options, disabled = props.disabled, readonly = props.readonly, header = props.header, emptyContent = props.emptyContent, optionsSort = props.optionsSort, className = props.className, children = props.children, _e = props.closeOnSelect, closeOnSelect = _e === void 0 ? true : _e, _f = props.flip, flip = _f === void 0 ? true : _f, onClick = props.onClick, onChange = props.onChange, onOpen = props.onOpen, onClose = props.onClose, restProps = __rest(props, ["defaultValue", "value", "placement", "mode", "mobile", "fullscreen", "matchWidth", "options", "disabled", "readonly", "header", "emptyContent", "optionsSort", "className", "children", "closeOnSelect", "flip", "onClick", "onChange", "onOpen", "onClose"]);
    var isFirstRender = React.useRef(true);
    var _g = __read(useBoolean(false), 2), visible = _g[0], setVisible = _g[1];
    var _h = __read(useBoolean(false), 2), focused = _h[0], setFocused = _h[1];
    var _j = __read(useStateCallback(isDefined(value) ? value : defaultValue), 2), innerValue = _j[0], setInnerValue = _j[1];
    var _k = __read(React.useState(options || []), 2), innerOptions = _k[0], setInnerOptions = _k[1];
    var _l = __read(React.useState(-1), 2), focusedIndex = _l[0], setFocusedIndex = _l[1];
    var isMultiple = mode === 'multiple';
    var cx = useStyles(modules_c052660e);
    var popoverClassNames = cx(className, 'container', {
        dropdown: !mobile,
    });
    var triggerElement;
    // Ensure Dropdown has only one child node
    var count = React.useMemo(function () { return React.Children.count(children); }, [children]);
    var condition = count === 0 || count > 1;
    if (condition) {
        error({
            condition: condition,
            message: 'Ensure Dropdown has only one child node',
        })();
        triggerElement = children;
    }
    else {
        var child_1 = React.Children.only(children);
        var handleClickTrigger = function (event) {
            if (disabled)
                return;
            !readonly && setVisible.toggle();
            setFocused.on();
            safeInvoke(child_1.props.onClick, event);
        };
        var handleFocusBlurTrigger = function (event) {
            if (event.type === 'focus') {
                setFocused.on();
                safeInvoke(child_1.props.onFocus, event);
            }
            else {
                setFocused.off();
                safeInvoke(child_1.props.onBlur, event);
            }
        };
        var triggerProps = __assign(__assign({}, child_1.props), { tabIndex: (_a = child_1.props.tabIndex) !== null && _a !== void 0 ? _a : 0, className: cx('triggerElement', child_1.props.className, { active: visible, disabled: disabled }), onClick: handleClickTrigger, onFocus: handleFocusBlurTrigger, onBlur: handleFocusBlurTrigger });
        triggerElement = React.cloneElement(child_1, triggerProps);
    }
    var activeOptions = React.useMemo(function () { return (innerOptions ? innerOptions.filter(function (option) { return !option.disabled; }) : []); }, [innerOptions]);
    var isActive = React.useCallback(function (value) {
        if (innerValue === undefined) {
            return false;
        }
        if (isMultiple && Array.isArray(innerValue)) {
            return innerValue.includes(value);
        }
        return innerValue === value;
    }, [innerValue, isMultiple]);
    var setValue = React.useCallback(function (value) {
        if (isMultiple && value !== undefined) {
            var pos = (innerValue || []).indexOf(value);
            var data = __spreadArray([], __read((innerValue || [])));
            pos >= 0 ? data.splice(pos, 1) : data.push(value);
            setInnerValue(data, function (val) {
                val && safeInvoke(onChange, val);
            });
            return;
        }
        setInnerValue(value, function (val) {
            isDefined(val) && safeInvoke(onChange, val);
        });
    }, [innerValue, isMultiple, onChange]);
    var closeMenuOnSelect = React.useCallback(function (value) { return (isDefined(closeOnSelect) && isFunction(closeOnSelect) ? closeOnSelect(value) : closeOnSelect); }, [closeOnSelect]);
    var handleSelect = React.useCallback(function (value, event) {
        setValue(value);
        event && safeInvoke(onClick, event);
        !isMultiple && closeMenuOnSelect(value) && setVisible.off();
    }, [isMultiple, closeMenuOnSelect, setValue]);
    var getCurrentIndex = React.useCallback(function (value) {
        if (!Array.isArray(innerOptions) || !value)
            return -1;
        if (isMultiple && Array.isArray(value)) {
            return activeOptions.findIndex(function (option) { return option.value === value[value.length - 1]; });
        }
        return activeOptions.findIndex(function (option) { return option.value === value; });
    }, [innerOptions, activeOptions, isMultiple]);
    var handleMouseMove = React.useCallback(function () {
        setFocusedIndex(getCurrentIndex(innerValue));
    }, [innerValue, getCurrentIndex]);
    var sortOptions = React.useCallback(function () {
        if (isNull(innerValue) || !options)
            return;
        var sortedOptions;
        if (Array.isArray(innerValue)) {
            var selectedOptions = innerValue
                .map(function (value) { return options.find(function (option) { return option.value === value; }); })
                .filter(function (option) { return option !== undefined; });
            sortedOptions = Array.from(new Set(__spreadArray(__spreadArray([], __read(selectedOptions)), __read(options))));
        }
        else {
            sortedOptions = __spreadArray([], __read(options)).sort(function (item) {
                if (item.value !== innerValue)
                    return 1;
                return -1;
            });
        }
        setInnerOptions(sortedOptions);
    }, [innerValue, options]);
    var getFocusedValue = React.useCallback(function () {
        var item = activeOptions[focusedIndex];
        return item ? item.value : null;
    }, [activeOptions, focusedIndex]);
    var menu = React.useMemo(function () {
        return (React.createElement(Menu, { className: cx('menu') }, innerOptions.map(function (_a) {
            var label = _a.label, value = _a.value, disabled = _a.disabled, args = __rest(_a, ["label", "value", "disabled"]);
            return (React.createElement(Menu.Item, __assign({}, args, { key: value, value: value, disabled: disabled, active: isActive(value) || getFocusedValue() === value, onClick: disabled ? noop : function (event) { return handleSelect(value, event); }, onMouseOver: handleMouseMove }), label));
        })));
    }, [innerOptions, isActive, getFocusedValue, handleMouseMove, handleSelect]);
    var content = React.useMemo(function () {
        if (innerOptions.length)
            return menu;
        return isDefined(emptyContent) ? (emptyContent) : (React.createElement(Menu, null,
            React.createElement(Menu.Item, { disabled: true },
                React.createElement(Text, { className: "h-text-center h-color-D60" }, defaultEmptyContentText))));
    }, [innerOptions, emptyContent, menu]);
    var defaultFooter = React.useMemo(function () { return (React.createElement(Button, { type: "button", color: "blue", variant: "primary", onClick: setVisible.off }, "\u0413\u043E\u0442\u043E\u0432\u043E")); }, [mobile]);
    useKeyPress({
        enabled: visible || focused,
        keyMap: {
            ArrowDown: function (event) {
                event.preventDefault();
                if (!visible) {
                    setVisible.on();
                    return;
                }
                var nextIndex = getNextIndex(focusedIndex, activeOptions.length);
                setFocusedIndex(nextIndex);
            },
            ArrowUp: function (event) {
                event.preventDefault();
                if (!visible) {
                    setVisible.on();
                    return;
                }
                var prevIndex = getPrevIndex(focusedIndex, activeOptions.length);
                setFocusedIndex(prevIndex);
            },
            Enter: function (event) {
                event.preventDefault();
                var value = getFocusedValue();
                if (value !== undefined && value !== null) {
                    handleSelect(value);
                }
            },
            Escape: function (event) {
                event.preventDefault();
                setVisible.off();
            },
            Tab: function () {
                setVisible.off();
                setFocused.off();
            },
        },
    }, [innerValue, focusedIndex, activeOptions, visible, focused, isMultiple, closeOnSelect]);
    React.useEffect(function () {
        if (visible) {
            safeInvoke(onOpen);
        }
        else {
            !isFirstRender.current && safeInvoke(onClose);
        }
        isFirstRender.current = false;
    }, [visible]);
    React.useEffect(function () {
        if (isDefined(value)) {
            setInnerValue(value);
        }
        else {
            setFocusedIndex(-1);
            !isFirstRender && setInnerValue(undefined);
        }
    }, [value]);
    React.useEffect(function () {
        if (visible) {
            setFocusedIndex(getCurrentIndex(innerValue));
        }
    }, [visible, innerValue, getCurrentIndex]);
    React.useEffect(function () {
        setInnerOptions(options || []);
    }, [options]);
    React.useEffect(function () {
        optionsSort && !visible && sortOptions();
    }, [visible, optionsSort, sortOptions]);
    return (React.createElement(Popover, __assign({ "data-qa": "Dropdown", ref: ref, className: popoverClassNames, visible: visible, header: mobile ? header : null, content: content, footer: mobile ? defaultFooter : null, mobile: mobile, positionerSettings: makePositionerSettings(placement, matchWidth, flip), onClose: setVisible.off, fullscreen: fullscreen }, restProps), triggerElement));
});
Dropdown.defaultProps = {
    placement: 'bottom-start',
    matchWidth: false,
    optionsSort: false,
    closeOnSelect: true,
    fullscreen: true,
};
Dropdown.displayName = 'Dropdown';

export default Dropdown;
export { defaultEmptyContentText };
//# sourceMappingURL=Dropdown.js.map
